'use client';

import { User } from '@/typings/user';
import React, { PropsWithChildren } from 'react';
import useFirebaseAuth, { RegisterData } from './hooks/useFirebaseAuth';
import { ConfirmationResult } from 'firebase/auth';

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  authError?: { login?: string; register?: string; reset?: string };
  login: (password: string, email: string) => Promise<void>;
  logout: (doNotDeleteFromLocalStorage: string[]) => void;
  signUp: (data: RegisterData) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmation: ConfirmationResult | null;
  handlePhoneLogin: (phone: string) => Promise<void>;
  handlePhoneConfirmation: (code: string) => Promise<void>;
  phoneNumber: string | null;
};

const AuthContext = React.createContext({} as AuthContextType);

const AuthProvider: React.FC<PropsWithChildren<{ initialUser?: User }>> = ({
  children,
  initialUser,
}) => {
  const [user, authError, login, logout, signUp, resetPassword, loading, phoneAuth] =
    useFirebaseAuth();

  return (
    <AuthContext.Provider
      value={{
        user: user || (initialUser as User),
        login,
        signUp,
        logout,
        loading,
        isAuthenticated: !!user,
        resetPassword,
        authError,
        confirmation: phoneAuth.confirmation,
        phoneNumber: phoneAuth.phoneNumber,
        handlePhoneLogin: phoneAuth.handlePhoneLogin,
        handlePhoneConfirmation: phoneAuth.handleValidateCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);

export default AuthProvider;
